import { PGliteProvider } from '@electric-sql/pglite-react';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import {
  BlockedScreen,
  Fatal,
  LockedScreen,
  NeedInternet,
  OnboardingPage,
  Preparing,
  SignInPage,
  SwitchUser,
} from './auth/screens';
import { configureApi } from './lib/api';
import { desktop } from './lib/desktop';
import { registerUiFonts } from './lib/fonts';
import { registerServiceWorker } from './lib/pwa';
import { WorkspaceContext } from './lib/workspace';
import { router } from './router';
import './styles.css';

registerUiFonts();
const d = desktop();
if (!d) void registerServiceWorker();

function Gate() {
  const { phase } = useAuth();
  switch (phase.kind) {
    case 'booting':
      return <Preparing message="Opening LabView…" />;
    case 'preparing':
      return <Preparing message={phase.message} />;
    case 'need_internet':
      return <NeedInternet />;
    case 'signed_out':
      return <SignInPage {...(phase.notice ? { notice: phase.notice } : {})} />;
    case 'locked':
      return (
        <LockedScreen
          name={phase.auth.userName}
          contact={phase.auth.email ?? phase.auth.phone ?? ''}
          labName={phase.auth.orgName}
          expired={phase.expired}
        />
      );
    case 'need_org':
      return <OnboardingPage userName={phase.userName} />;
    case 'blocked':
      return <BlockedScreen blocked={phase.blocked} />;
    case 'switch_user':
      return <SwitchUser pending={phase.pending} />;
    case 'fatal':
      return <Fatal message={phase.message} />;
    case 'ready':
      return (
        <PGliteProvider db={phase.db}>
          <WorkspaceContext.Provider
            value={{
              db: phase.db,
              orgId: phase.auth.orgId,
              siteId: phase.auth.siteId,
              auth: phase.auth,
              engine: phase.engine,
            }}
          >
            <RouterProvider router={router} />
          </WorkspaceContext.Provider>
        </PGliteProvider>
      );
  }
}

/** In the desktop app, requests carry a saved token instead of a cookie, and go to the lab's server. */
async function setUpDesktop() {
  if (!d) return;
  let token: string | null = await d.vault.getToken().catch(() => null);
  configureApi({ baseUrl: d.config.apiUrl, getToken: () => token });
  window.addEventListener('qr-token', (e) => {
    token = (e as CustomEvent<string | null>).detail;
  });
}

const el = document.getElementById('root');
await setUpDesktop();
if (el) {
  createRoot(el).render(
    <StrictMode>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </StrictMode>,
  );
}
